import { useCallback } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { MemberOverviewMapper, MemberTransactionMapper, UserAPIResponseMapper } from '@/api/mapper/user';
import { getMemberTransactions, getUser, getUsers } from '@/api/service/user';
import type { PageQuery } from '@/api/type/payment';

export const memberKeys = {
  all: ['members'] as const,
  list: (query: PageQuery) => [...memberKeys.all, 'list', query] as const,
  detail: (memberId: string) => [...memberKeys.all, 'detail', memberId] as const,
  transactions: (memberId: string) => [...memberKeys.all, 'transactions', memberId] as const,
};

/** One page of the Members table, with the total and the summary cards (for all members). */
export function useMembersQuery(query: PageQuery, enabled = true) {
  return useQuery({
    queryKey: memberKeys.list(query),
    queryFn: async () => {
      const response = await getUsers(query);
      return {
        members: (response.data ?? []).filter((user) => !user.is_superuser).map(UserAPIResponseMapper),
        total: response.total ?? 0,
        overview: MemberOverviewMapper(response.summary),
      };
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Every policy, loan and balance transaction of one member, for the Transaction History popup. */
export function useMemberTransactionsQuery(memberId?: string | null, enabled = true) {
  return useQuery({
    queryKey: memberKeys.transactions(memberId ?? 'none'),
    queryFn: async () => ((await getMemberTransactions(Number(memberId))).data ?? []).map(MemberTransactionMapper),
    enabled: enabled && Boolean(memberId),
  });
}

/** One member's full record (contacts, documents, photo), for View and Edit Member. */
export function useMemberQuery(memberId?: string | null, enabled = true) {
  return useQuery({
    queryKey: memberKeys.detail(memberId ?? 'none'),
    queryFn: async () => UserAPIResponseMapper(await getUser(Number(memberId))),
    enabled: enabled && Boolean(memberId),
  });
}

/** Refresh the members list and any open member record. */
export function useInvalidateMembers() {
  const queryClient = useQueryClient();

  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: memberKeys.all,
      }),
    [queryClient]
  );
}
